import {
  createContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Resizable } from "react-resizable";
import { Button, Dropdown, Checkbox, Space } from "antd";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import SettingsIcon from "@/icons/SettingsIcon";
import DragHandle from "@/components/ui/DragHandle";

const SortableColumnContext = createContext({});

const SortableColumn = ({ columnKey, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: columnKey });

  const contextValue = useMemo(
    () => ({ attributes, listeners, setActivatorNodeRef }),
    [attributes, listeners, setActivatorNodeRef],
  );

  return (
    <SortableColumnContext.Provider value={contextValue}>
      <div
        ref={setNodeRef}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          transform: CSS.Translate.toString(transform),
          transition,
          position: "relative",
          zIndex: isDragging ? 1 : undefined,
          background: "#fff",
        }}
      >
        {children}
      </div>
    </SortableColumnContext.Provider>
  );
};

const getColumnOrder = (columns, preferredOrder = [], fixedEndKeys = []) => {
  const availableKeys = columns
    .map((column) => column?.key)
    .filter((key) => key && !fixedEndKeys.includes(key));
  const availableKeySet = new Set(availableKeys);
  const order = [];

  for (const key of preferredOrder) {
    if (availableKeySet.has(key) && !order.includes(key)) order.push(key);
  }

  for (const key of availableKeys) {
    if (!order.includes(key)) order.push(key);
  }

  return order;
};

/**
 * Загружает конфигурацию колонок из localStorage
 * @param {string} user_uuid - Уникальный идентификатор пользователя
 * @param {string} localStorageKey - Ключ для localStorage
 * @returns {Object} Объект с ширинами и скрытыми колонками
 */
function loadColumnsConfig(user_uuid, localStorageKey) {
  try {
    // Формируем ключ для localStorage с учетом пользователя
    const raw = localStorage.getItem(`${user_uuid}_${localStorageKey}`);
    if (!raw) return { widths: {}, hidden: {}, order: [] };

    const parsed = JSON.parse(raw);
    return {
      widths: parsed?.widths || {}, // Ширины колонок
      hidden: parsed?.hidden || {}, // Скрытые колонки
      order: Array.isArray(parsed?.order) ? parsed.order : [], // Порядок колонок
    };
  } catch (error) {
    console.error("Error loading columns config:", error);
    // В случае ошибки возвращаем пустую конфигурацию
    return { widths: {}, hidden: {}, order: [] };
  }
}

/**
 * Сохраняет конфигурацию колонок в localStorage
 * @param {Object} next - Новая конфигурация для сохранения
 * @param {string} user_uuid - Уникальный идентификатор пользователя
 * @param {string} localStorageKey - Ключ для localStorage
 */
function saveColumnsConfig(next, user_uuid, localStorageKey) {
  try {
    // Загружаем предыдущую конфигурацию
    const prev = loadColumnsConfig(user_uuid, localStorageKey);

    // Объединяем старую и новую конфигурацию
    const merged = {
      widths: { ...prev.widths, ...(next.widths || {}) },
      hidden: { ...prev.hidden, ...(next.hidden || {}) },
      order: Array.isArray(next.order) ? next.order : prev.order,
    };

    // Сохраняем в localStorage
    localStorage.setItem(
      `${user_uuid}_${localStorageKey}`,
      JSON.stringify(merged),
    );
  } catch (error) {
    console.error("Error saving columns config:", error);
  }
}

/**
 * Ячейка заголовка с ручкой ресайза
 */
export const ResizableTitle = (props) => {
  const { onResize, width, ...restProps } = props;

  if (!width) return <th {...restProps} />;

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          onClick={(e) => e.stopPropagation()}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} />
    </Resizable>
  );
};

/**
 * Хук для управления конфигурацией колонок таблицы
 * @param {Array} baseColumns - Базовые колонки таблицы
 * @param {string} userUuid - Уникальный идентификатор пользователя
 * @param {string} localStorageKey - Ключ для сохранения в localStorage
 * @param {Object} options - Дополнительные настройки
 * @param {boolean} options.resize - Включить ресайз колонок
 * @param {Object} options.columnLabels - Лейблы для чекбоксов
 * @param {Array} options.excludeKeys - Ключи колонок, которые нельзя скрывать
 * @param {boolean} options.reorder - Включить изменение порядка колонок
 * @param {Array} options.defaultOrder - Порядок колонок по умолчанию
 * @param {Array} options.fixedEndKeys - Колонки, всегда закрепленные в конце
 * @returns {Object} Объект с колонками и компонентами таблицы
 */
export const useColumnConfig = (
  baseColumns,
  userUuid,
  localStorageKey,
  {
    resize = false,
    columnLabels = {},
    dynamicColumns = [],
    excludeKeys = ["actions", "readOnly", "check"],
    reorder = false,
    defaultOrder = [],
    fixedEndKeys = ["actions"],
  } = {},
) => {
  // Загружаем сохраненную конфигурацию при инициализации
  const storedConfig = loadColumnsConfig(userUuid, localStorageKey);

  // Таймер для отложенного сохранения изменений ширины
  const saveTimer = useRef(null);

  const safeBaseColumns = useMemo(
    () => (Array.isArray(baseColumns) ? baseColumns : []),
    [baseColumns],
  );

  const [columnOrder, setColumnOrder] = useState(() =>
    getColumnOrder(
      safeBaseColumns,
      storedConfig.order.length ? storedConfig.order : defaultOrder,
      fixedEndKeys,
    ),
  );

  // Состояние ширины колонок
  const [colWidths, setColWidths] = useState(() => {
    const init = {};
    // Инициализируем ширины для каждой колонки
    for (const col of safeBaseColumns) {
      if (col?.key) {
        // Берем сохраненную ширину или значение по умолчанию
        const defaultWidths =
          typeof col.width === "number" ? col.width : undefined;
        init[col.key] = storedConfig.widths[col.key] ?? defaultWidths;
      }
    }
    return init;
  });

  // Состояние скрытых колонок
  const [hidden, setHidden] = useState(() => {
    const init = { ...storedConfig.hidden };

    for (const col of [...safeBaseColumns, ...dynamicColumns]) {
      if (!col?.key) continue;
      if (init[col.key] == null) {
        init[col.key] = col.defaultVisible === false;
      }
    }

    return init;
  });

  /**
   * Функция для отложенного сохранения ширины колонок
   * Использует таймер для предотвращения частых записей в localStorage
   */
  const scheduleSaveWidths = useCallback(
    (nextWidths) => {
      // Отменяем предыдущий таймер, если он существует
      if (saveTimer.current) clearTimeout(saveTimer.current);

      // Устанавливаем новый таймер на 500мс
      saveTimer.current = setTimeout(() => {
        saveColumnsConfig({ widths: nextWidths }, userUuid, localStorageKey);
      }, 500);
    },
    [userUuid, localStorageKey],
  );

  /**
   * Обработчик изменения размера колонки
   */
  const handleResize = useCallback(
    (key) =>
      (_e, { size }) => {
        const min = 80; // Минимальная ширина колонки
        setColWidths((prev) => {
          // Устанавливаем новую ширину с учетом минимального значения
          const next = { ...prev, [key]: Math.max(min, size.width) };
          // Планируем сохранение
          scheduleSaveWidths(next);
          return next;
        });
      },
    [scheduleSaveWidths],
  );

  /**
   * Переключение видимости колонки
   */
  const toggleColumnVisibility = useCallback(
    (key) => {
      if (excludeKeys.includes(key)) return;

      setHidden((prev) => {
        // Инвертируем состояние видимости
        const next = { ...prev, [key]: !prev[key] };
        // Сохраняем изменения немедленно
        saveColumnsConfig({ hidden: next }, userUuid, localStorageKey);
        return next;
      });
    },
    [excludeKeys, userUuid, localStorageKey],
  );

  /**
   * Проверка, видима ли колонка
   */
  const isColumnVisible = useCallback((key) => hidden[key] !== true, [hidden]);

  /**
   * Dropdown с чекбоксами для управления видимостью колонок
   */
  const allColumnsForSettings = useMemo(() => {
    const columns = [
      ...safeBaseColumns
        .filter(
          (column) =>
            columnLabels[column.key] && !fixedEndKeys.includes(column.key),
        )
        .map((c) => ({ key: c.key, label: columnLabels[c.key] })),
      ...dynamicColumns,
    ];

    if (!reorder) return columns;

    const orderIndex = new Map(
      columnOrder.map((columnKey, index) => [columnKey, index]),
    );
    return [...columns].sort(
      (a, b) =>
        (orderIndex.get(a.key) ?? Number.MAX_SAFE_INTEGER) -
        (orderIndex.get(b.key) ?? Number.MAX_SAFE_INTEGER),
    );
  }, [
    safeBaseColumns,
    columnLabels,
    dynamicColumns,
    fixedEndKeys,
    reorder,
    columnOrder,
  ]);

  const handleDragEnd = useCallback(
    ({ active, over }) => {
      if (!over || active.id === over.id) return;

      setColumnOrder((prev) => {
        const oldIndex = prev.indexOf(active.id);
        const newIndex = prev.indexOf(over.id);
        if (oldIndex === -1 || newIndex === -1) return prev;

        const next = arrayMove(prev, oldIndex, newIndex);
        saveColumnsConfig({ order: next }, userUuid, localStorageKey);
        return next;
      });
    },
    [userUuid, localStorageKey],
  );

  const defaultColumnOrder = useMemo(
    () => getColumnOrder(safeBaseColumns, defaultOrder, fixedEndKeys),
    [safeBaseColumns, defaultOrder, fixedEndKeys],
  );

  const isDefaultOrder =
    columnOrder.length === defaultColumnOrder.length &&
    columnOrder.every((key, index) => key === defaultColumnOrder[index]);

  const resetColumnOrder = useCallback(() => {
    setColumnOrder(defaultColumnOrder);
    saveColumnsConfig(
      { order: defaultColumnOrder },
      userUuid,
      localStorageKey,
    );
  }, [defaultColumnOrder, userUuid, localStorageKey]);

  const settingsItems = useMemo(
    () =>
      allColumnsForSettings.map(({ key, label }) => {
        const visibilityLocked = excludeKeys.includes(key);
        const checkbox = (
          <Checkbox
            checked={visibilityLocked || isColumnVisible(key)}
            disabled={visibilityLocked}
            onChange={() => toggleColumnVisibility(key)}
          >
            {label}
          </Checkbox>
        );

        if (!reorder) return <div key={key}>{checkbox}</div>;

        return (
          <SortableColumn key={key} columnKey={key}>
            <DragHandle context={SortableColumnContext} />
            {checkbox}
          </SortableColumn>
        );
      }),
    [
      allColumnsForSettings,
      excludeKeys,
      isColumnVisible,
      reorder,
      toggleColumnVisibility,
    ],
  );

  const columnsSettingsDropdown = useMemo(() => {
    return (
      <Dropdown
        trigger={["click"]}
        placement="bottomRight"
        popupRender={() => (
          <div
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 4,
              boxShadow:
                "0 6px 16px rgba(0, 0, 0, 0.08), 0 3px 6px rgba(0, 0, 0, 0.12)",
              maxHeight: 320,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div style={{ padding: 16, minHeight: 0, overflowY: "auto" }}>
              {reorder ? (
                <DndContext
                  modifiers={[restrictToVerticalAxis]}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={allColumnsForSettings.map(({ key }) => key)}
                    strategy={verticalListSortingStrategy}
                  >
                    <Space orientation="vertical">{settingsItems}</Space>
                  </SortableContext>
                </DndContext>
              ) : (
                <Space orientation="vertical">{settingsItems}</Space>
              )}
            </div>
            {reorder && (
              <div
                style={{
                  padding: "8px 16px 12px",
                  borderTop: "1px solid #f0f0f0",
                  background: "#fff",
                }}
              >
                <Button
                  type="link"
                  size="small"
                  disabled={isDefaultOrder}
                  onClick={resetColumnOrder}
                  style={{ paddingInline: 0 }}
                >
                  Сбросить порядок
                </Button>
              </div>
            )}
          </div>
        )}
      >
        <span style={{ display: "inline-flex" }}>
          <SettingsIcon
            style={{ fontSize: 18, color: "#BBBBBB", cursor: "pointer" }}
          />
        </span>
      </Dropdown>
    );
  }, [
    allColumnsForSettings,
    handleDragEnd,
    isDefaultOrder,
    reorder,
    resetColumnOrder,
    settingsItems,
  ]);

  /**
   * Формирует итоговые колонки для antd Table
   * - фильтрует скрытые
   * - подставляет width
   * - добавляет onHeaderCell для resize
   * - подменяет title у actions на dropdown
   */
  const columns = useMemo(() => {
    const ordered = reorder
      ? [
          ...columnOrder
            .map((key) => safeBaseColumns.find((column) => column.key === key))
            .filter(Boolean),
          ...safeBaseColumns.filter(
            (column) =>
              !columnOrder.includes(column.key) &&
              !fixedEndKeys.includes(column.key),
          ),
          ...fixedEndKeys
            .map((key) => safeBaseColumns.find((column) => column.key === key))
            .filter(Boolean),
        ]
      : safeBaseColumns;

    const filtered = ordered.filter((col) => {
      if (!col?.key) return true;
      if (excludeKeys.includes(col.key)) return true;
      return hidden[col.key] !== true;
    });

    return filtered.map((col) => {
      // Колонка с настройками
      if (col.key === "actions") {
        return { ...col, title: columnsSettingsDropdown };
      }

      // Без ресайза
      if (!resize || !col?.key || excludeKeys.includes(col.key)) return col;

      const width =
        colWidths[col.key] ??
        (typeof col.width === "number" ? col.width : undefined);

      return {
        ...col,
        width,
        onHeaderCell: () => ({
          width,
          onResize: handleResize(col.key),
        }),
      };
    });
  }, [
    safeBaseColumns,
    reorder,
    columnOrder,
    fixedEndKeys,
    hidden,
    resize,
    colWidths,
    handleResize,
    excludeKeys,
    columnsSettingsDropdown,
  ]);

  /**
   * components для Table (ResizableTitle)
   */
  const components = useMemo(
    () => ({
      header: {
        cell: ResizableTitle,
      },
    }),
    [],
  );

  // Эффект для синхронизации состояния с изменениями baseColumns
  useEffect(() => {
    if (reorder) {
      setColumnOrder((prev) => {
        const next = getColumnOrder(safeBaseColumns, prev, fixedEndKeys);
        if (
          next.length === prev.length &&
          next.every((key, index) => key === prev[index])
        ) {
          return prev;
        }

        saveColumnsConfig({ order: next }, userUuid, localStorageKey);
        return next;
      });
    }

    // Обновляем состояние скрытых колонок
    setHidden((prev) => {
      const next = { ...prev };
      let changed = false;

      // Добавляем новые колонки как видимые
      for (const col of safeBaseColumns) {
        if (!col?.key) continue;
        if (next[col.key] == null) {
          next[col.key] = false;
          changed = true;
        }
      }

      // Сохраняем изменения если они были
      if (changed)
        saveColumnsConfig({ hidden: next }, userUuid, localStorageKey);
      return changed ? next : prev;
    });

    // Обновляем состояние ширины колонок
    setColWidths((prev) => {
      const stored = loadColumnsConfig(userUuid, localStorageKey).widths;
      const next = { ...prev };
      let changed = false;

      // Добавляем ширины для новых колонок
      for (const col of safeBaseColumns) {
        if (!col?.key) continue;
        if (next[col.key] == null) {
          next[col.key] =
            stored[col.key] ??
            (typeof col.width === "number" ? col.width : undefined);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [
    safeBaseColumns,
    userUuid,
    localStorageKey,
    reorder,
    fixedEndKeys,
  ]);

  // Очистка таймера при размонтировании компонента
  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    },
    [],
  );

  // Возвращаем готовые данные для Table
  return {
    columns,
    components,
  };
};
